import React, { useState, useEffect } from 'react';
import { 
  User, 
  Phone, 
  MapPin, 
  Calendar, 
  Edit2,
  LogOut,
  Store,
  Shield,
  Package,
  Bell,
  X
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { colorMap } from '../modules/colorMap';
import { useAuth } from '../contexts/AuthContext';
import { useNotifications } from '../contexts/NotificationsContext';

export default function AccountPage() {
  const [showNotifications, setShowNotifications] = useState(false);
  const [loadingNotifications, setLoadingNotifications] = useState(false);
  const navigate = useNavigate();
  const { userInfo, loading, logout } = useAuth();
  const {
    unreadCount,
    notifications,
    fetchNotifications,
    markAsRead,
    deleteNotification,
  } = useNotifications();

  // Charger les notifications
  const loadNotifications = async () => {
    setLoadingNotifications(true);
    try {
      await fetchNotifications();
    } catch (error) {
      console.error('Erreur chargement notifications:', error);
    } finally {
      setLoadingNotifications(false);
    }
  };
const getNotificationStyle = (type) => {
  switch (type) {
    case 'success':
      return {
        icon: '✓',
        bgColor: '#ecfdf5',
        iconBg: '#10b981',
        borderColor: '#a7f3d0',
        textColor: '#065f46'
      };
    case 'error':
      return {
        icon: '✕',
        bgColor: '#fef2f2',
        iconBg: '#ef4444',
        borderColor: '#fecaca',
        textColor: '#991b1b'
      };
    case 'warning':
      return {
        icon: '!',
        bgColor: '#fffbeb',
        iconBg: '#f59e0b',
        borderColor: '#fde68a',
        textColor: '#92400e'
      };
    case 'info':
    default:
      return {
        icon: 'i',
        bgColor: '#eff6ff',
        iconBg: '#3b82f6',
        borderColor: '#bfdbfe',
        textColor: '#1e40af'
      };
  }
};


  // Ouvrir/fermer le panneau de notifications
  const toggleNotifications = () => {
    if (!showNotifications) {
      loadNotifications();
    }
    setShowNotifications(!showNotifications);
  };

  // Formater la date
  const formatDate = (dateString) => {
    if (!dateString) return 'Date inconnue';
    const date = new Date(dateString);
    return date.toLocaleDateString('fr-FR', {
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    });
  };

  // Formater l'heure relative
  const formatRelativeTime = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'À l\'instant';
    if (diffMins < 60) return `Il y a ${diffMins} min`;
    if (diffHours < 24) return `Il y a ${diffHours}h`;
    if (diffDays < 7) return `Il y a ${diffDays}j`;
    return date.toLocaleDateString('fr-FR');
  };

  // Fonction de déconnexion
  const handleLogout = async () => {
    await logout();
  };

  // Fonction pour éditer le profil
  const handleEditProfile = () => {
    alert('La modification du profil n\'est pas encore disponible.\nContactez le support au 0546641390 pour toute modification.');
  };

  // Rediriger si non connecté
  useEffect(() => {
    if (!loading && !userInfo) {
      navigate('/auth');
    }
  }, [loading, userInfo, navigate]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: colorMap.bg.light }}>
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-t-2 border-b-2" 
               style={{ borderColor: colorMap.secondary.base }}></div>
          <p className="mt-4" style={{ color: colorMap.text.secondary }}>
            Chargement du profil...
          </p>
        </div>
      </div>
    );
  }

  if (!userInfo) {
    return null;
  }

  return (
    <div className="min-h-screen pb-20" style={{ backgroundColor: colorMap.bg.light }}>
      {/* Header */}
      <header 
        className="sticky top-0 z-10 px-4 py-4 flex items-center justify-between"
        style={{ 
          backgroundColor: colorMap.bg.white,
          borderBottom: `1px solid ${colorMap.border.default}`
        }}
      >
        <h1 className="text-2xl font-bold" style={{ color: colorMap.text.primary }}>
          Mon Profil
        </h1>
        <button
          onClick={toggleNotifications}
          className="relative p-2 rounded-lg transition-colors hover:bg-gray-100"
          style={{ color: colorMap.text.secondary }}
        >
          <Bell size={20} />
          {unreadCount > 0 && (
            <span 
              className="absolute -top-1 -right-1 bg-red-500 text-white text-[10px] font-bold rounded-full min-w-[18px] h-[18px] flex items-center justify-center px-1"
            >
              {unreadCount > 99 ? '99+' : unreadCount}
            </span>
          )}
        </button>
      </header>

      {/* Panneau de notifications */}
      {showNotifications && (
        <>
          {/* Overlay */}
          <div 
            className="fixed inset-0 bg-black bg-opacity-50 z-40"
            onClick={toggleNotifications}
          />
          
          {/* Panneau latéral */}
          <div 
            className="fixed top-0 right-0 h-full w-full max-w-md bg-white z-50 shadow-2xl overflow-y-auto"
            style={{ 
              animation: 'slideInRight 0.3s ease-out',
              backgroundColor: colorMap.bg.white 
            }}
          >
            {/* Header du panneau */}
            <div 
              className="sticky top-0 px-4 py-4 flex items-center justify-between border-b"
              style={{ 
                backgroundColor: colorMap.bg.white,
                borderColor: colorMap.border.default 
              }}
            >
              <h2 className="text-xl font-bold" style={{ color: colorMap.text.primary }}>
                Notifications
              </h2>
              <button
                onClick={toggleNotifications}
                className="p-2 rounded-lg hover:bg-gray-100 transition-colors"
                style={{ color: colorMap.text.secondary }}
              >
                <X size={20} />
              </button>
            </div>

            {/* Liste des notifications */}
            <div className="p-4">
              {loadingNotifications ? (
                <div className="flex justify-center py-8">
                  <div 
                    className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2"
                    style={{ borderColor: colorMap.secondary.base }}
                  />
                </div>
              ) : notifications.length === 0 ? (
                <div className="text-center py-12">
                  <Bell size={48} className="mx-auto mb-4 opacity-20" />
                  <p style={{ color: colorMap.text.muted }}>
                    Aucune notification
                  </p>
                </div>
              ) : (
// Fonction pour obtenir le style selon le type


// La div des notifications
<div className="space-y-3">
  {notifications.map((notif, index) => {
    const style = getNotificationStyle(notif.type);

    return (
      <div
        key={notif.id}
        onClick={() => !notif.is_viewed && markAsRead(notif.id)}
        className="group relative overflow-hidden rounded-2xl transition-all duration-300 hover:shadow-lg cursor-pointer"
        style={{
          backgroundColor: notif.is_viewed ? colorMap.bg.white : style.bgColor,
          border: `1.5px solid ${notif.is_viewed ? colorMap.border.default : style.borderColor}`,
          transform: 'translateZ(0)'
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.transform = 'translateY(-2px)';
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.transform = 'translateY(0)';
        }}
      >
        {/* Badge indicateur non lu */}
        {!notif.is_viewed && (
          <div 
            className="absolute top-3 right-3 w-2 h-2 rounded-full animate-pulse"
            style={{ backgroundColor: style.iconBg }}
          />
        )}

        <div className="flex items-start gap-3 p-4">
          {/* Icône */}
          <div 
            className="flex-shrink-0 w-12 h-12 rounded-xl flex items-center justify-center text-white font-bold text-lg shadow-md"
            style={{ 
              backgroundColor: notif.is_viewed ? colorMap.text.muted : style.iconBg
            }}
          >
            {style.icon}
          </div>

          {/* Contenu */}
          <div className="flex-1 min-w-0">
            {/* Titre */}
            <h4 
              className={`text-sm font-semibold mb-1 ${notif.is_viewed ? '' : 'text-gray-900'}`}
              style={{ 
                color: notif.is_viewed ? colorMap.text.secondary : style.textColor
              }}
            >
              {notif.title}
            </h4>

            {/* Message */}
            <p 
              className={`text-sm leading-relaxed mb-2 ${notif.is_viewed ? 'text-gray-500' : 'text-gray-700'}`}
              style={{ color: notif.is_viewed ? colorMap.text.muted : colorMap.text.primary }}
            >
              {notif.message}
            </p>

            {/* Footer avec date et badge */}
            <div className="flex items-center justify-between">
              <span 
                className="text-xs"
                style={{ color: colorMap.text.muted }}
              >
                {formatRelativeTime(notif.created_at)}
              </span>

              {!notif.is_viewed && (
                <span 
                  className="text-xs font-semibold px-2 py-1 rounded-full"
                  style={{ 
                    backgroundColor: style.iconBg,
                    color: '#fff'
                  }}
                >
                  Nouveau
                </span>
              )}
            </div>
          </div>

          {/* Bouton supprimer (apparaît au hover) */}
          <button
            onClick={(e) => {
              e.stopPropagation();
              // Fonction pour supprimer la notification
              deleteNotification(notif.id);
            }}
            className="flex-shrink-0 opacity-0 group-hover:opacity-100 transition-opacity p-2 rounded-lg hover:bg-red-50"
            style={{ color: '#ef4444' }}
          >
            <X size={16} />
          </button>
        </div>
      </div>
    );
  })}
</div>
              )}
            </div>
          </div>
        </>
      )}

      {/* Section profil */}
      <div className="px-4 py-6">
        {/* Carte profil */}
        <div 
          className="rounded-2xl p-6 mb-6 shadow-sm"
          style={{ 
            backgroundColor: colorMap.bg.white,
            border: `1px solid ${colorMap.border.default}`
          }}
        >
          <div className="flex items-start justify-between mb-6">
            <div className="flex items-center gap-4">
              {/* Avatar */}
              <div 
                className="w-20 h-20 rounded-full flex items-center justify-center text-3xl font-bold shadow-md"
                style={{ 
                  backgroundColor: colorMap.secondary.light,
                  color: colorMap.secondary.dark
                }}
              >
                {userInfo.name ? userInfo.name.charAt(0).toUpperCase() : 'U'}
              </div>
              
              <div>
                <div className="flex items-center gap-2 mb-1">
                  <h2 className="text-xl font-bold" style={{ color: colorMap.text.primary }}>
                    {userInfo.name || userInfo.phone}
                  </h2>
                  {userInfo.shopName && (
                    <span 
                      className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs"
                      style={{ 
                        backgroundColor: colorMap.primary.light,
                        color: colorMap.primary.base
                      }}
                    >
                      <Store size={12} />
                      Marchand
                    </span>
                  )}
                </div>
                
                <p className="text-sm" style={{ color: colorMap.text.secondary }}>
                  Membre depuis {formatDate(userInfo.createdAt)}
                </p>
              </div>
            </div>

            <button
              onClick={handleEditProfile}
              className="p-2 rounded-lg transition-colors hover:bg-gray-100"
              style={{ color: colorMap.secondary.base }}
            >
              <Edit2 size={20} />
            </button>
          </div>

          {/* Bouton Mes Produits */}
          <button
            onClick={() => navigate('/posts')}
            className="w-full py-3 rounded-xl font-semibold flex items-center justify-center gap-2 transition-all duration-200"
            style={{
              backgroundColor: colorMap.secondary.base,
              color: colorMap.bg.white
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = colorMap.secondary.hover;
              e.currentTarget.style.transform = 'scale(1.02)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = colorMap.secondary.base;
              e.currentTarget.style.transform = 'scale(1)';
            }}
          >
            <Package size={20} />
            Gérer mes produits
          </button>
        </div>

        {/* Informations personnelles */}
        <div 
          className="rounded-2xl p-6 shadow-sm mb-6"
          style={{ 
            backgroundColor: colorMap.bg.white,
            border: `1px solid ${colorMap.border.default}`
          }}
        >
          <h3 className="text-lg font-semibold mb-4" style={{ color: colorMap.text.primary }}>
            Informations personnelles
          </h3>

          <div className="space-y-4">
            {/* Téléphone */}
            <div className="flex items-center gap-3">
              <div 
                className="p-2 rounded-lg"
                style={{ backgroundColor: colorMap.bg.light }}
              >
                <Phone size={18} style={{ color: colorMap.secondary.base }} />
              </div>
              <div>
                <div className="text-sm" style={{ color: colorMap.text.muted }}>Téléphone</div>
                <div className="text-base font-medium" style={{ color: colorMap.text.primary }}>
                  {userInfo.phone || 'Non renseigné'}
                </div>
              </div>
            </div>

            {/* Email si disponible */}
            {userInfo.email && (
              <div className="flex items-center gap-3">
                <div 
                  className="p-2 rounded-lg"
                  style={{ backgroundColor: colorMap.bg.light }}
                >
                  <User size={18} style={{ color: colorMap.secondary.base }} />
                </div>
                <div>
                  <div className="text-sm" style={{ color: colorMap.text.muted }}>Email</div>
                  <div className="text-base font-medium" style={{ color: colorMap.text.primary }}>
                    {userInfo.email}
                  </div>
                </div>
              </div>
            )}

            {/* Boutique */}
            {userInfo.shopName && (
              <div className="flex items-center gap-3">
                <div 
                  className="p-2 rounded-lg"
                  style={{ backgroundColor: colorMap.bg.light }}
                >
                  <Store size={18} style={{ color: colorMap.secondary.base }} />
                </div>
                <div>
                  <div className="text-sm" style={{ color: colorMap.text.muted }}>Boutique</div>
                  <div className="text-base font-medium" style={{ color: colorMap.text.primary }}>
                    {userInfo.shopName}
                  </div>
                </div>
              </div>
            )}

            {/* Localisation */}
            {(userInfo.site || userInfo.chambre) && (
              <div className="flex items-center gap-3">
                <div 
                  className="p-2 rounded-lg"
                  style={{ backgroundColor: colorMap.bg.light }}
                >
                  <MapPin size={18} style={{ color: colorMap.secondary.base }} />
                </div>
                <div>
                  <div className="text-sm" style={{ color: colorMap.text.muted }}>Localisation</div>
                  <div className="text-base font-medium" style={{ color: colorMap.text.primary }}>
                    {`${userInfo.site || ''} ${userInfo.batiment ? `- Bât ${userInfo.batiment}` : ''} ${userInfo.chambre ? `- Chambre ${userInfo.chambre}` : ''}`.trim()}
                  </div>
                </div>
              </div>
            )}

            {/* Date d'inscription */}
            <div className="flex items-center gap-3">
              <div 
                className="p-2 rounded-lg"
                style={{ backgroundColor: colorMap.bg.light }}
              >
                <Calendar size={18} style={{ color: colorMap.secondary.base }} />
              </div>
              <div>
                <div className="text-sm" style={{ color: colorMap.text.muted }}>Inscrit depuis</div>
                <div className="text-base font-medium" style={{ color: colorMap.text.primary }}>
                  {formatDate(userInfo.createdAt)}
                </div>
              </div>
            </div>

            {/* Statut */}
            <div className="flex items-center gap-3">
              <div 
                className="p-2 rounded-lg"
                style={{ backgroundColor: colorMap.bg.light }}
              >
                <Shield size={18} style={{ color: colorMap.primary.base }} />
              </div>
              <div>
                <div className="text-sm" style={{ color: colorMap.text.muted }}>Statut</div>
                <div className="text-base font-medium" style={{ color: colorMap.text.primary }}>
                  {userInfo.shopName ? 'Marchand vérifié' : 'Utilisateur'}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Déconnexion */}
        <button
          onClick={handleLogout}
          className="w-full flex items-center justify-center gap-2 py-3 rounded-xl font-semibold transition-all duration-200"
          style={{
            backgroundColor: '#fee2e2',
            color: '#dc2626',
            border: '1px solid #fecaca'
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.backgroundColor = '#fecaca';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.backgroundColor = '#fee2e2';
          }}
        >
          <LogOut size={20} />
          Se déconnecter
        </button>
      </div>

      {/* Espace pour le bottom tab */}
      <div className="h-4" />

      {/* Animation CSS pour le slide */}
      <style>{`
        @keyframes slideInRight {
          from {
            transform: translateX(100%);
          }
          to {
            transform: translateX(0);
          }
        }
      `}</style>
    </div>
  );
}